import { contractApi } from '../contractApi';
import { CptView, FeeSummaryView } from '../types/contractTypes';
import { CptGetRequest } from '../requests/cptGetRequest';
import { CptsByFeeScheduleIdRequest } from '../requests/cptsByFeeScheduleIdRequest';
import { CptUpsertRequest } from '../requests/cptUpsertRequest';

const cptEndpoints = contractApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get CPT by ID
    getCpt: builder.query<CptView, CptGetRequest>({
      query: ({ id }) => ({
        url: 'CptGetById',
        params: { id },
      }),
      providesTags: (result, error, { id }) => [{ type: 'Cpt', id }, 'Cpt'],
    }),

    // Get all CPTs by fee schedule ID
    getCptsByFeeScheduleId: builder.query<CptView[], CptsByFeeScheduleIdRequest>({
      query: ({ feeScheduleId }) => ({
        url: 'Cpts',
        method: 'GET',
        params: { feeScheduleId },
      }),
      providesTags: (result, error, { feeScheduleId }) => [
        { type: 'CptList', id: feeScheduleId },
        'Cpt',
      ],
    }),

    // Upsert CPT (create or update)
    upsertCpt: builder.mutation<CptView, CptUpsertRequest>({
      query: (body) => ({
        url: 'Cpt',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        ...(body.cpt.id ? [{ type: 'Cpt' as const, id: body.cpt.id }] : []),
        { type: 'CptList', id: body.cpt.feeScheduleId },
        'Cpt',
        'CptList',
      ],
    }),

    // Delete CPT
    deleteCpt: builder.mutation<void, { id: string; feeScheduleId: string }>({
      query: ({ id }) => ({
        url: 'CptDelete',
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: (result, error, { feeScheduleId }) => [
        { type: 'CptList', id: feeScheduleId },
        'Cpt',
        'CptList',
      ],
    }),

    // Upload CPTs via CSV file
    uploadCpts: builder.mutation<FeeSummaryView, { feeScheduleId: string; file: File }>({
      query: ({ feeScheduleId, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `FeeSchedule/${feeScheduleId}/Cpts/Upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { feeScheduleId }) => [
        { type: 'CptList', id: feeScheduleId },
        { type: 'FeeSchedule', id: feeScheduleId },
        'Cpt',
        'CptList',
        'FeeSchedule',
      ],
    }),

    // Download CPTs as CSV file
    downloadCpts: builder.mutation<Blob, { feeScheduleId: string }>({
      query: ({ feeScheduleId }) => ({
        url: `FeeSchedule/${feeScheduleId}/Cpts/Download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCptQuery,
  useGetCptsByFeeScheduleIdQuery,
  useUpsertCptMutation,
  useDeleteCptMutation,
  useUploadCptsMutation,
  useDownloadCptsMutation,
} = cptEndpoints;
